/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Product, ProductId } from 'utils/Product';
import ReactLoading from 'react-loading';
import { useParams } from 'react-router-dom';
import { Button } from 'components/common/Button';
import ProductViewer from '../ProductViewer';
import ProductEditorTable from './ProductEditorTable';
import { FormState, useForm, resetForm } from 'components/common/form-utils/form-state';
import { validateForm, Validators } from 'components/common/form-utils/validators';
import { requests } from 'api-services';
import { UpdatedProduct } from 'api-services/product-requests';

interface ProductEditorProps {
  productId: ProductId
}

export const ProductEditor = ({ productId }: ProductEditorProps) => {
  const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [baseProduct, setBaseProduct] = useState<Product | undefined>(undefined);
  const [submitState, setSubmitState] = useState<{ status: 'error' | 'done' | 'loading', message?: string } | null>(null);
  
  const form: FormState = useForm({
    title: {
      visibleName: 'Title',
      requiredIf: (val) => val !== undefined },
    category: {
      visibleName: 'Category',
      requiredIf: (val) => val !== undefined },
    image: {
      visibleName: 'Image',
      requiredIf: (val) => val !== undefined },
    stars: {
      visibleName: 'Stars',
      requiredIf: (val) => val !== undefined,
      validations: [Validators.isInteger(), Validators.minValue(1), Validators.maxValue(5)] },
    price: {
      visibleName: 'Price',
      requiredIf: (val) => val !== undefined,
      validations: [Validators.isNumber(), Validators.minValue(0)] },
    quantity: {
      visibleName: 'Quantity',
      requiredIf: (val) => val !== undefined,
      validations: [Validators.isInteger(), Validators.minValue(0)] },
    visible: {
      visibleName: 'Visible',
      requiredIf: (val) => val !== undefined,
      type: 'boolean',
      validations: [] },
  });

  useEffect(() => {
    requests.product.get(productId)
      .then((p) => {
        setBaseProduct(p);
        setState('ready');
      })
      .catch(e => {
        console.warn(`Can't edit product: ${e}`);
        setState('error');
      });
  }, []);

  const getProduct = (): Partial<Product> => {
    const product: Partial<Product> = {};
    if (form.title.value && !form.title.error) product.title = form.title.value;
    if (form.category.value && !form.category.error) product.category = form.category.value;
    if (form.image.value && !form.image.error) product.image = form.image.value;
    if (form.stars.value && !form.stars.error) product.stars = Number.parseInt(form.stars.value);
    if (form.price.value && !form.price.error) product.price = Number.parseFloat(form.price.value);
    if (form.quantity.value && !form.quantity.error) product.quantity = Number.parseInt(form.quantity.value);
    if (form.visible.value && !form.visible.error) product.visible = form.visible.getBoolean();
    return product;
  };

  const applyProductEdits = (): Product => {
    if (!baseProduct) throw new Error('baseProduct not set before applying edits');
    return {
      ...baseProduct,
      ...getProduct(),
    };
  };

  const onSubmit = () => {
    // Prevent submission if no edits have been made.
    if (Object.values(form).filter(field => field.value !== undefined).length === 0) {
      setSubmitState({ status: 'error', message: 'No edits have been made' });
      return;
    }

    if (!validateForm(form)) {
      setSubmitState({ status: 'error', message: 'Product field modifications have errors.' });
      return;
    }

    setSubmitState({ status: 'loading' });
    const product = applyProductEdits() as UpdatedProduct;

    // The backend expects an image only if we are updating the image,
    // so we must remove it from the result if it has not been changed.
    if (!form.image.value) delete product.image;

    requests.product.put(product)
      .then((p) => {
        setBaseProduct(p);
        resetForm(form);
        setSubmitState({ status: 'done' });
      })
      .catch((e) => setSubmitState({ status: 'error', message: `Bad request: ${e}` }));
  };

  return <React.Fragment>
    {state === 'loading' && <ReactLoading className='m-auto' type={'bubbles'} color={'#ac542e'} height={'50%'} width={'8%'} />}
    {state === 'error' && <span className='text-lg my-8'>An error occurred.</span>}
    {state === 'ready' && baseProduct
      && <div className='my-8'>
        <div role='heading' className='text-2xl pb-4 font-medium'>Edit product &quot;{baseProduct?.title}&quot;</div>
        <div className=' flex justify-center w-120 font-medium mt-8'>
          <ProductViewer id='originalProduct' name='Original Product' product={baseProduct} />
          <ProductViewer id='editedProduct' name='After Edits' product={applyProductEdits()} />
        </div>
        <div className='max-w-max mx-auto'>
          <ProductEditorTable baseProduct={baseProduct} productEdits={form} />
          {submitState && submitState.status === 'loading'
            && <ReactLoading className='m-auto' type={'bubbles'} color={'#ac542e'} height={'50%'} width={'8%'} />}
          {submitState && submitState.status === 'error'
            && <div className='text-s text-red-600 mb-4'>
              {submitState.message
                ? <>An error occurred updating the product: {submitState.message}</>
                : <>An error occurred updating the product.</>}
            </div>}
          {submitState && submitState.status === 'done'
            && <div className='text-s text-green-200 mb-4'>Successfully updated product.</div> }
          {(!submitState || submitState.status !== 'loading')
            && <div className='flex flex-row w-full place-content-end space-x-1 pr-4'>
              <Button grey id='cancel-button' linkTo='/admin/products'>Cancel</Button>
              <Button danger id='submit-modifications-button' onClick={onSubmit}>Submit Modifications</Button>
            </div>}
        </div>
      </div>}
  </React.Fragment>;
};

const ProductEditorPage = () => {
  const { id } = useParams<{ id: string }>();
  return <ProductEditor productId={id} />;
};

export default ProductEditorPage;
